import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MButton } from './MButtons';
import MDialog from './MDialog';
import { useExitToParent } from '@/hooks/useExitToParent';
import { useTranslation } from 'react-i18next';
import { useDetectPageInteraction } from '@/hooks/useDetectPageInteraction';

interface MExitProps {
  path?: string;
  exitKey?: string;
  hideExit?: boolean;
  className?: string;
  level?: number;
  skipConfirmation?: boolean;
}

/**
 * MExit - Atom for Exit button with confirmation dialog.
 * @param path - Optional path to navigate to on exit. If not provided, uses useExitToParent (2 levels up)
 * @param hideExit - If true, hides the Exit button entirely
 * @param className - Optional className for positioning
 * @param skipConfirmation - If true, skips the confirmation dialog and exits immediately
 * Shows confirmation only if the user has typed or changed something on the page.
 */
export const MExit: React.FC<MExitProps> = ({ path, hideExit = false, className, level = 2, exitKey, skipConfirmation = false }) => {
  const [showExitDialog, setShowExitDialog] = useState(false);
  const navigate = useNavigate();
  const exitToParent = useExitToParent(level); // 2 levels up
  const { t } = useTranslation();

  // Hook detects whether user interacted with any input, select, or keyboard
  const [dirty, setDirty] = useDetectPageInteraction();

  if (hideExit) return null;

  const handleExitClick = () => {
    if (skipConfirmation || !dirty) {
      setDirty(false);
      if (path) navigate(path);
      else exitToParent();
    } else {
      setShowExitDialog(true);
    }
  };

  const handleCancel = () => setShowExitDialog(false);
  const handleProceed = () => {
    setShowExitDialog(false);
    setDirty(false);
    if (path) {
      navigate(path);
    } else {
      exitToParent();
    }
  };

  const dialogKey = exitKey || 'exit';
  const exitTitle = t(`common.exitDialog.${dialogKey}.title`);
  const exitDescription = t(`common.exitDialog.${dialogKey}.description`);
  const exitContinue = t('common.exitDialog.continue');
  const exitProceed = t('common.exitDialog.proceed');

  return (
    <>
      <MButton
        variant="link"
        size="md"
        className={className}
        onClick={handleExitClick}
        style={{zIndex: 99}}
      >
        <span style={{ color: '#2A2A2A', fontSize: 16, fontWeight: 600 }}>Exit</span>
      </MButton>
      <MDialog
        isOpen={showExitDialog}
        onClose={handleCancel}
        title={exitTitle}
        description={exitDescription}
        primaryCtaText={exitProceed}
        onPrimaryCta={handleProceed}
        secondaryCtaText={exitContinue}
        onSecondaryCta={handleCancel}
      />
    </>
  );
};
